import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * AdminUsdcChart — Real-time USDC/USD chart for the admin dashboard.
 *
 * Uses CoinGecko API for price data and renders a canvas-based chart.
 * Shows 24h price history with live updates every 30 seconds.
 */
const AdminUsdcChart = () => {
    const canvasRef = useRef(null);
    const [prices, setPrices] = useState([]);
    const [currentPrice, setCurrentPrice] = useState(null);
    const [change24h, setChange24h] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchPrices = useCallback(async () => {
        try {
            // 24h history from CoinGecko
            const res = await fetch(
                'https://api.coingecko.com/api/v3/coins/usd-coin/market_chart?vs_currency=usd&days=1'
            );
            if (!res.ok) return;
            const data = await res.json();
            const pts = data.prices.map(([t, p]) => ({ t, p }));
            setPrices(pts);

            if (pts.length > 1) {
                setCurrentPrice(pts[pts.length - 1].p);
                setChange24h(
                    ((pts[pts.length - 1].p - pts[0].p) / pts[0].p) * 100
                );
            }
            setLoading(false);
        } catch {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPrices();
        const interval = setInterval(fetchPrices, 30000);
        return () => clearInterval(interval);
    }, [fetchPrices]);

    // Draw chart
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || prices.length < 2) return;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        ctx.scale(dpr, dpr);

        // Clear
        ctx.clearRect(0, 0, w, h);

        const pVals = prices.map(p => p.p);
        const minP = Math.min(...pVals);
        const maxP = Math.max(...pVals);
        const range = maxP - minP || 0.001;
        const pad = 8;

        // Grid lines
        ctx.strokeStyle = 'rgba(255,255,255,0.04)';
        ctx.lineWidth = 0.5;
        for (let i = 0; i < 5; i++) {
            const y = pad + ((h - pad * 2) * i) / 4;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(w, y);
            ctx.stroke();
        }

        // Gradient fill
        const gradient = ctx.createLinearGradient(0, 0, 0, h);
        const isUp = change24h >= 0;
        if (isUp) {
            gradient.addColorStop(0, 'rgba(16, 185, 129, 0.25)');
            gradient.addColorStop(1, 'rgba(16, 185, 129, 0.01)');
        } else {
            gradient.addColorStop(0, 'rgba(239, 68, 68, 0.25)');
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0.01)');
        }

        // Build path
        const points = prices.map((p, i) => ({
            x: (i / (prices.length - 1)) * w,
            y: pad + (1 - (p.p - minP) / range) * (h - pad * 2),
        }));

        // Fill area
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.lineTo(w, h);
        ctx.lineTo(0, h);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();

        // Line stroke
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.strokeStyle = isUp ? '#10b981' : '#ef4444';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Current price dot
        const last = points[points.length - 1];
        ctx.beginPath();
        ctx.arc(last.x, last.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = isUp ? '#10b981' : '#ef4444';
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.stroke();
    }, [prices, change24h]);

    return (
        <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-black text-white">
                        USDC/USD
                    </span>
                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 font-bold uppercase tracking-wider border border-blue-500/20">
                        24H
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-white font-bold">
                        {currentPrice ? `$${currentPrice.toFixed(4)}` : '—'}
                    </span>
                    <span className={`text-[10px] font-mono font-bold ${change24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {change24h >= 0 ? '+' : ''}{change24h.toFixed(4)}%
                    </span>
                </div>
            </div>
            <div className="relative" style={{ height: '180px' }}>
                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-gray-600 text-[10px] font-mono animate-pulse">Loading USDC data...</span>
                    </div>
                ) : (
                    <canvas
                        ref={canvasRef}
                        className="w-full h-full"
                        style={{ width: '100%', height: '100%' }}
                    />
                )}
            </div>
            <div className="flex justify-between mt-2">
                <span className="text-[8px] text-gray-600 font-mono">24h ago</span>
                <span className="text-[8px] text-gray-600 font-mono">Now</span>
            </div>
        </div>
    );
};

export default AdminUsdcChart;
